<template>
    <RedesignBaseChart
        ref="baseChartRef"
        :widget="data"
        :option="chartOptions"
        classes="h-full min-h-52"
    >
        <template #post>
            <div class="px-3">
                <div
                    v-for="item in chartData"
                    :key="`${item.name}-${extension.userID}`"
                >
                    <extension-dashboard-list-item
                        :color="item.itemStyle.color"
                        :label="item.name"
                        :value="item.value"
                    />
                </div>
            </div>
            <div
                style="border-radius: 20px"
                class="bg-gray-100 w-max mx-auto flex p-1.5 justify-center gap-2"
            >
                <div>
                    <redesign-user-status
                        text-size="12px"
                        :extension-status-data="extensionStatusData"
                        :extension="extension"
                    />
                </div>
                <div>
                    <redesign-status-duration
                        text-size="12px"
                        :settings="settings"
                        :extension="extension"
                        @recently-logged-out-expired="emit('recently-logged-out-expired')"
                    />
                </div>
            </div>
        </template>
    </RedesignBaseChart>
</template>

<script setup lang="ts">
import { computed, toRef } from 'vue'
import useExtensionStatus from '@/composables/useExtensionStatus'

const TOTAL_ANSWERED_COLOR = '#4CAF50'
const TOTAL_UNANSWERED_COLOR = '#FF4D4D'

import type { Settings, WidgetExtended } from '@/types/dashboard'
import type { ECBasicOption } from 'echarts/types/dist/shared'
import RedesignBaseChart from '@/components/RedesignComponents/Charts/RedesignBaseChart.vue'
import RedesignUserStatus from '@/components/RedesignComponents/RedesignTableComponents/RedesignUserStatus.vue'
import { MergedExtension } from '@/types/extension'
import RedesignStatusDuration from '@/components/RedesignComponents/RedesignTableComponents/RedesignStatusDuration.vue'
import ExtensionDashboardListItem
    from '@/components/RedesignComponents/Cards/ExtensionsDashbordCards/ExtensionDashboardListItem.vue'
import i18n from '@/i18n'

/* Props */
const props = defineProps<{
    data: WidgetExtended
    extension: MergedExtension
    settings: Settings
}>()

/* Emits */
const emit = defineEmits<{
    (event: 'recently-logged-out-expired'): void
}>()

const extensionRef = toRef(props, 'extension')
const settingsRef = toRef(props, 'settings')

const { extensionStatusData } = useExtensionStatus(extensionRef, settingsRef)

const chartData = computed(() => {
    return [
        {
            value: props.extension?.allDataSummary?.TotalAnswered ?? 0,
            name: i18n.t('call.answered'),
            itemStyle: { color: TOTAL_ANSWERED_COLOR },
            label: {
                color: TOTAL_ANSWERED_COLOR
            }
        },
        {
            value: +(props.extension?.allDataSummary?.TotalCallsCount) - +(props.extension?.allDataSummary?.TotalAnswered),
            name: i18n.t('call.unanswered'),
            itemStyle: { color: TOTAL_UNANSWERED_COLOR },
            label: {
                color: TOTAL_UNANSWERED_COLOR
            }
        }
    ].filter(item => item.value)
})

/* Computed */
const dialedLabelTextColor = computed(() => {
    return getComputedStyle(document.documentElement).getPropertyValue('--steel').trim()
})
const chartOptions = computed<ECBasicOption>(() => ({
    tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} ({d}%)',
        position: 'inside',
    },
    legend: {
        show: false
    },
    graphic: [
        {
            type: 'text',
            left: 'center',
            top: '44%',
            style: {
                text: i18n.t('calls.totalDialed').toUpperCase(),
                textAlign: 'center',
                fontSize: 9,
                fontWeight: 600,
                fill: dialedLabelTextColor.value
            }
        },
        {
            type: 'text',
            left: 'center',
            top: '52%',
            style: {
                text: props.extension?.allDataSummary?.TotalCallsCount,
                textAlign: 'center',
                fontSize: 18,
                fontWeight: 700
            }
        }
    ],
    series: [
        {
            type: 'pie',
            minAngle: 5,
            padAngle: 2,
            radius: [ '40%', '55%' ],
            center: [ '50%', '50%' ],
            avoidLabelOverlap: false,
            label: {
                show: true,
                position: 'outside',
                formatter: ({ percent }: {percent: number}) => `${percent.toFixed(1)}%`,
                fontSize: 12,
                lineHeight: 140
            },
            labelLine: {
                show: true,
                length: 8,
                length2: 4,
                smooth: true,
                lineStyle: {
                    width: 1
                },
                maxSurfaceAngle: 90
            },
            emphasis: {
                itemStyle: {
                    shadowBlur: 10,
                    shadowOffsetX: 0,
                }
            },
            data: chartData.value
        }
    ],
}))
</script>

<style lang="scss" scoped>
</style>
